import { DeactivatableButton } from '@folio/components';
import { useToast } from '@folio/components/ui/use-toast';
import { useAssertedParams } from '@folio/lib';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useMutation } from '@tanstack/react-query';
import {
  brokerageFirmDtoFirmTypeEnum,
  policyControllerConfirmPolicy,
  policyControllerPushPolicyEvent,
} from 'korint-sdk';
import { MailIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFinalizeNewBusinessRules } from '../useFinalizeNewBusinessRules';
import { useVisibilityConditionsProps } from '../useVisibilityConditionsProps';

export function PushCustomEventButton({ overridenBranchId }: { overridenBranchId?: string }) {
  const { t } = useTranslation();

  const { toast } = useToast();
  const { policyId, branchId: assertedBranchId } = useAssertedParams();
  const branchId = overridenBranchId ?? assertedBranchId;
  const { mutate: refetchPolicyOnDashboard } = usePolicyOnDashboard();

  const visibilityConditionsProps = useVisibilityConditionsProps({
    action: 'CONFIRM',
    overridenBranchId,
  });

  const { mutate: sendToClient, isPending } = useMutation({
    mutationFn: async () => {
      if (visibilityConditionsProps?.policyStatus !== 'POLICY_CONFIRMED') {
        await policyControllerConfirmPolicy(policyId, { branchId });
      }
      await policyControllerPushPolicyEvent(
        policyId,
        {
          eventType:
            visibilityConditionsProps?.branchType === 'MTA'
              ? 'AXECE_MTA_SIGNATURE_REQUESTED'
              : 'AXECE_NB_SIGNATURE_REQUESTED',
        },
        { branchId },
      );
    },
    onSuccess: async () => {
      toast({
        title: t([
          'finalizeNewBusiness.buttons.push-custom-event.successMessage',
          'finalizeNewBusiness.defaultSuccessMessage',
        ]),
      });
      await refetchPolicyOnDashboard();
    },
  });

  const rules = useFinalizeNewBusinessRules({ overridenBranchId });

  if (!rules) {
    return null;
  }

  if (!visibilityConditionsProps) {
    return null;
  }

  const showButton =
    visibilityConditionsProps.firmType === brokerageFirmDtoFirmTypeEnum.WHOLESALE &&
    visibilityConditionsProps.isDerogationSatisfied &&
    visibilityConditionsProps.branchStatus !== 'BRANCH_CLOSED' &&
    visibilityConditionsProps.policyStatus === 'POLICY_QUOTED';

  if (!showButton) {
    return null;
  }

  return (
    <DeactivatableButton
      variant="ghost"
      rules={rules}
      icon={<MailIcon className="h-4 w-4" />}
      isLoading={isPending}
      onClick={() => sendToClient()}
    >
      <span>{t('finalizeNewBusiness.buttons.push-custom-event.label')}</span>
    </DeactivatableButton>
  );
}
